/**
 * Role-Based Access Control (RBAC) System
 * Manages permissions and access control for different user roles
 */

import { User } from '@/types';

// Define permissions
export type Permission =
  | 'view_dashboard'
  | 'view_organizations'
  | 'create_organization'
  | 'edit_organization'
  | 'delete_organization'
  | 'view_calculations'
  | 'create_calculation'
  | 'edit_calculation'
  | 'delete_calculation'
  | 'view_reports'
  | 'generate_report'
  | 'export_report'
  | 'view_configuration'
  | 'edit_configuration'
  | 'view_users'
  | 'approve_users'
  | 'manage_users'
  | 'system_admin';

// Define role permissions mapping
const ROLE_PERMISSIONS: Record<User['role'], Permission[]> = {
  super_admin: [
    'view_dashboard',
    'view_organizations',
    'create_organization',
    'edit_organization',
    'delete_organization',
    'view_calculations',
    'create_calculation',
    'edit_calculation',
    'delete_calculation',
    'view_reports',
    'generate_report',
    'export_report',
    'view_configuration',
    'edit_configuration',
    'view_users',
    'approve_users',
    'manage_users',
    'system_admin',
  ],
};

// Resource-based permissions (for future expansion)
export interface ResourcePermission {
  resource: string;
  action: string;
  resourceId?: string;
}

/**
 * Role-Based Access Control Manager
 */
export class RBACManager {
  private static instance: RBACManager;

  private constructor() {}

  public static getInstance(): RBACManager {
    if (!RBACManager.instance) {
      RBACManager.instance = new RBACManager();
    }
    return RBACManager.instance;
  }

  /**
   * Check if user has a specific permission
   */
  public hasPermission(user: User | null, permission: Permission): boolean {
    if (!user || user.status !== 'approved') {
      return false;
    }

    const userPermissions = ROLE_PERMISSIONS[user.role] || [];
    return userPermissions.includes(permission);
  }

  /**
   * Check if user has multiple permissions (all required)
   */
  public hasAllPermissions(
    user: User | null,
    permissions: Permission[]
  ): boolean {
    return permissions.every(permission =>
      this.hasPermission(user, permission)
    );
  }

  /**
   * Check if user has any of the specified permissions
   */
  public hasAnyPermission(
    user: User | null,
    permissions: Permission[]
  ): boolean {
    return permissions.some(permission => this.hasPermission(user, permission));
  }

  /**
   * Get all permissions for a user
   */
  public getUserPermissions(user: User | null): Permission[] {
    if (!user || user.status !== 'approved') {
      return [];
    }

    return ROLE_PERMISSIONS[user.role] || [];
  }

  /**
   * Check if user can access a specific resource
   */
  public canAccessResource(
    user: User | null,
    _resource: string,
    _action: string,
    _resourceId?: string
  ): boolean {
    if (!user || user.status !== 'approved') {
      return false;
    }

    // For MVP, all super_admins have access to all resources
    // This can be expanded later for more granular permissions
    return user.role === 'super_admin';
  }

  /**
   * Validate user access for route/component
   */
  public validateAccess(
    user: User | null,
    requiredPermissions: Permission[],
    requireAll: boolean = true
  ): {
    hasAccess: boolean;
    missingPermissions: Permission[];
  } {
    if (!user) {
      return {
        hasAccess: false,
        missingPermissions: requiredPermissions,
      };
    }

    const userPermissions = this.getUserPermissions(user);
    const missingPermissions = requiredPermissions.filter(
      permission => !userPermissions.includes(permission)
    );

    const hasAccess = requireAll
      ? missingPermissions.length === 0
      : missingPermissions.length < requiredPermissions.length;

    return {
      hasAccess,
      missingPermissions,
    };
  }

  /**
   * Filter menu items based on user permissions
   */
  public filterMenuItems<T extends { requiredPermissions?: Permission[] }>(
    user: User | null,
    menuItems: T[]
  ): T[] {
    return menuItems.filter(item => {
      if (!item.requiredPermissions || item.requiredPermissions.length === 0) {
        return true;
      }
      return this.hasAllPermissions(user, item.requiredPermissions);
    });
  }

  /**
   * Check if user is admin
   */
  public isAdmin(user: User | null): boolean {
    return user?.role === 'super_admin' && user.status === 'approved';
  }

  /**
   * Check if user can perform administrative actions
   */
  public canPerformAdminActions(user: User | null): boolean {
    return this.hasPermission(user, 'system_admin');
  }
}

// Export singleton instance
export const rbacManager = RBACManager.getInstance();

// Convenience hooks for React components
export const usePermissions = (user: User | null) => {
  return {
    hasPermission: (permission: Permission) =>
      rbacManager.hasPermission(user, permission),
    hasAllPermissions: (permissions: Permission[]) =>
      rbacManager.hasAllPermissions(user, permissions),
    hasAnyPermission: (permissions: Permission[]) =>
      rbacManager.hasAnyPermission(user, permissions),
    getUserPermissions: () => rbacManager.getUserPermissions(user),
    canAccessResource: (
      resource: string,
      action: string,
      resourceId?: string
    ) => rbacManager.canAccessResource(user, resource, action, resourceId),
    isAdmin: () => rbacManager.isAdmin(user),
    canPerformAdminActions: () => rbacManager.canPerformAdminActions(user),
  };
};
